'use client';

import { useEffect, useMemo, useState } from 'react';

import { Button } from '@nextui-org/react';
import { Check, ChevronRight, Download, ShoppingCart } from 'lucide-react';

import {
  useAddMasterclassesToLocalStorageCartMutation,
  useAddMasterclassToCartMutation,
} from '@/entities/cart/query';
import { checkInCartLocalStorage } from '@/entities/cart/utils';
import { IMasterclass } from '@/entities/masterclass/types';
import { sendYMEvent, YM_TARGETS_ENUM } from '@/shared/const/ym';
import { useIsAuthorizated } from '@/shared/hooks/is-authorizated';
import { useRouter } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { cn } from '@/shared/utils/utils';
import { useDownloadMasterclassMutation } from '@/entities/masterclass/query';
import { Link } from '@/shared/navigation';

export const McButton = ({
  masterclass,
  className,
  primary: _primary = false,
  size = 'md',
  isLoading: isLoadingProp,
  /** На карточке в списке МК не показываем CTA «Отзыв — промокод» */
  showReviewPromoButton = true,
  /** Компактный вид — только иконка (для карточки рядом с ценой) */
  iconOnly = false,
}: {
  masterclass: IMasterclass;
  className?: string;
  primary?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Напр. refetch МК после гидрации (inCart / isBuyed) */
  isLoading?: boolean;
  showReviewPromoButton?: boolean;
  iconOnly?: boolean;
}) => {
  const isBuyed = masterclass?.isBuyed;
  const { mutate: addToCart, isPending } = useAddMasterclassToCartMutation();
  const router = useRouter();
  const { isAuthorizated } = useIsAuthorizated();
  /**
   * localStorage недоступен на сервере, поэтому на первом клиентском рендере
   * учитываем только `masterclass.inCart` (совпадает с SSR), а гостевую
   * корзину из localStorage подмешиваем уже после монтирования — иначе
   * получаем hydration mismatch.
   */
  const [inCartLocalStorage, setInCartLocalStorage] = useState(false);
  useEffect(() => {
    setInCartLocalStorage(checkInCartLocalStorage(masterclass.id) ?? false);
  }, [masterclass.id]);
  /**
   * Списки МК на этой странице приходят через SSR/ISR-пропсы, а не useQuery,
   * поэтому invalidateQueries(masterclasses) в мутации их не обновляет —
   * держим локальный оверрайд, чтобы кнопка сразу отражала добавление в корзину.
   */
  const [addedToCartLocally, setAddedToCartLocally] = useState(false);
  useEffect(() => {
    setAddedToCartLocally(false);
  }, [masterclass.id]);
  const inCart =
    masterclass?.inCart || inCartLocalStorage || addedToCartLocally;
  const { mutate: addItemLocalStorage } =
    useAddMasterclassesToLocalStorageCartMutation();
  const { mutate: downloadMasterclass, isPending: isPendingDownload } =
    useDownloadMasterclassMutation();

  const title = useMemo(() => {
    if (masterclass.isFree) {
      return 'Скачать';
    }
    if (isBuyed) {
      return 'Мои мастер-классы';
    }
    if (inCart) {
      return 'Перейти в корзину';
    }
    if (!isBuyed && !inCart) {
      return 'В корзину';
    }
  }, [masterclass, isBuyed, inCart]);
  const onClickHandler = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    // if (!isAuthorizated) {
    //   return message.info(
    //     ' Чтобы добавить товар в корзину, требуется авторизация',
    //   );
    // }
    if (masterclass.isFree) {
      return downloadMasterclass({
        mcId: masterclass.id,
      });
    }
    if (isBuyed) {
      return router.push(ROUTES.meMasterclasses);
    }
    if (inCart) {
      return router.push(ROUTES.cart);
    }
    if (!isBuyed && !inCart) {
      sendYMEvent(YM_TARGETS_ENUM.ADD_TO_CART);
      if (isAuthorizated) {
        addToCart(masterclass.id, {
          onSuccess: () => setAddedToCartLocally(true),
        });
      } else {
        addItemLocalStorage(masterclass.id, {
          onSuccess: () => setInCartLocalStorage(true),
        });
      }
    }
  };

  const icon = useMemo(() => {
    if (masterclass.isFree) {
      return <Download strokeWidth={1.5} />;
    }
    if (isBuyed) {
      return <Check strokeWidth={1.5} />;
    }
    if (inCart) {
      return <ChevronRight strokeWidth={1.5} />;
    }
    return <ShoppingCart strokeWidth={1.5} />;
  }, [masterclass.isFree, isBuyed, inCart]);

  const variant = useMemo(() => {
    if (!inCart && !isBuyed) {
      return 'shadow';
    }
    // if (inCart) {
    //   return 'flat';
    // }
    return 'bordered';
  }, [isBuyed, inCart]);

  const reviewHref = `/masterclasses/${masterclass.wordId}/add-review`;

  if (iconOnly) {
    return (
      <Button
        isIconOnly
        radius="full"
        aria-label={title}
        title={title}
        isLoading={isPending || isPendingDownload || Boolean(isLoadingProp)}
        className={cn(
          'shrink-0',
          !inCart && !isBuyed
            ? 'text-gray-800'
            : 'text-[#fdc64d] border-[#fdc64d]',
          className,
        )}
        onClick={onClickHandler}
        color="primary"
        variant={variant}
        size={size}
      >
        {icon}
      </Button>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2 w-full', className)}>
      <Button
        radius="full"
        isLoading={isPending || isPendingDownload || Boolean(isLoadingProp)}
        className={cn(
          'w-full',
          !inCart && !isBuyed
            ? 'text-gray-800'
            : 'text-[#fdc64d] border-[#fdc64d]',
        )}
        onClick={onClickHandler}
        color="primary"
        variant={variant}
        size={size}
      >
        {title}
      </Button>
      {showReviewPromoButton ? (
        <Button
          as={Link}
          href={reviewHref}
          radius="full"
          variant="bordered"
          size={size}
          className="w-full border-default-300 text-default-700"
        >
          Отзыв — промокод 10%
        </Button>
      ) : null}
    </div>
  );
};
